"use client";

import { api } from "@/api";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { useToast } from "@/component/Toast/useToast";
import { userStore } from "@/store/user";
import { Modal } from "antd";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import Button from "../Button";
import CloseIcon from "./assets/close.svg";

interface RedeemCodeModalProps {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_PLACEHOLDER = "Enter your code";

export const RedeemCodeModal: React.FC<RedeemCodeModalProps> = ({
  open,
  onClose,
}) => {
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const toast = useToast();

  const handleClose = () => {
    onClose();
    // Reset state on close
    setTimeout(() => {
      setCode("");
      setHasError(false);
      setIsLoading(false);
    }, 300);
  };

  const handleConfirm = async () => {
    if (!code || isLoading) return;

    logger.event(VidMuseEvent.RedeemCodeModalSureClick);
    setIsLoading(true);
    setHasError(false);

    try {
      await api.redemptionCodes.useRedemptionCode({ code });
      toast.success("Redeemed successfully");
      void userStore.fetchUser();
      handleClose();
    } catch (err) {
      setCode("");
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasError) {
      setHasError(false);
    }
    setCode(e.target.value);
  };

  useEffect(() => {
    if (!open) {
      setHasError(false);
      setCode("");
      setIsLoading(false);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      centered
      closable={false}
      footer={null}
      width={420}
      classNames={{
        content: "!bg-[#32303F] !rounded-[20px] !p-0 !overflow-hidden",
      }}
    >
      <div className="relative px-6 pt-9 pb-10 h-[170px] flex flex-col justify-between">
        <button
          type="button"
          aria-label="Close"
          className="absolute top-4 right-4 cursor-pointer bg-transparent border-none p-0"
          onClick={handleClose}
        >
          <CloseIcon className="w-5 h-5 text-white/20 hover:text-white/60 transition-colors" />
        </button>

        <div className="text-center text-xl font-semibold text-white">
          Redeem a Code
        </div>

        <div className="flex items-center gap-3">
          <div
            className={classNames(
              "flex-1 h-10 bg-[#292933] rounded-full flex items-center px-5 border transition-all",
              hasError ? "border-[#FF5959]" : "border-transparent",
            )}
          >
            <input
              type="text"
              value={code}
              disabled={isLoading}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirm();
                }
              }}
              placeholder={
                isFocused
                  ? ""
                  : hasError
                    ? "Invalid redemption code."
                    : DEFAULT_PLACEHOLDER
              }
              className={classNames(
                "w-full bg-transparent outline-none text-white text-sm placeholder:text-xs",
                hasError && !isFocused
                  ? "placeholder:text-[#FF5959]"
                  : "placeholder:text-white/40",
              )}
            />
          </div>
          <Button
            type="primary"
            className="px-6 h-10 text-sm font-medium"
            loading={isLoading}
            onClick={handleConfirm}
          >
            Redeem
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RedeemCodeModal;
